import {
  useCheckInPatientMutation,
  useCreateScheduleAppointmentMutation,
  useDeleteScheduleAppointmentMutation,
  useNotifyMultiplePatientMutation,
  useNotifyPatientMutation,
  useUpdateScheduleAppointmentMutation,
} from 'features/Schedule/api/schedule.api'

import { useNotification } from 'app/providers/notification/NotificationProvider'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import type { ScheduleAppointmentDto } from 'features/Schedule/api/schedule.types'
import { useMemo } from 'react'
import { useParams } from 'react-router'

export const useScheduleWeekActions = () => {
  const { siteId } = useParams()
  const { setNotification } = useNotification()
  const [createScheduleAppointment, { isLoading: isCreating }] =
    useCreateScheduleAppointmentMutation()
  const [updateScheduleAppointment, { isLoading: isUpdating }] =
    useUpdateScheduleAppointmentMutation()

  const [notifySchedulePatient, { isLoading: isNotifying }] = useNotifyPatientMutation()
  const [notifyMultipleSchedulePatients, { isLoading: isNotifyingMultiple }] =
    useNotifyMultiplePatientMutation()
  const [deleteScheduleAppointment, { isLoading: isDeleting }] =
    useDeleteScheduleAppointmentMutation()

  const [checkInSchedulePatient, { isLoading: isCheckingIn }] = useCheckInPatientMutation()

  const handleError = (error: any) => {
    const data = error?.data
    const message = Array.isArray(data) ? data.join(', ') : data?.message
    const errorMessage = message || 'An error occurred'
    setNotification({
      title: 'Error',
      description: errorMessage,
      type: ALERT_CONSTANTS.ERROR,
    })
  }

  const createAppointment = async (data: ScheduleAppointmentDto, date: string) => {
    try {
      return await createScheduleAppointment({
        siteId,
        date,
        data,
      }).unwrap()
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  const updateAppointment = async (
    data: ScheduleAppointmentDto,
    date: string,
    appointmentId: number,
  ) => {
    try {
      return await updateScheduleAppointment({
        siteId,
        date,
        appointmentId,
        data,
      }).unwrap()
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  const notifyPatient = async (appointmentId: number) => {
    try {
      return await notifySchedulePatient({
        siteId,
        appointmentId,
      }).unwrap()
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  const notifyMultiplePatients = async (appointmentIds: number[]) => {
    try {
      await notifyMultipleSchedulePatients({
        siteId,
        appointmentIds,
      }).unwrap()
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  const deleteAppointment = async (date: string, appointmentId: number, sendSms?: number) => {
    try {
      return await deleteScheduleAppointment({
        siteId,
        date,
        appointmentId,
        sendSms,
      }).unwrap()
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  const checkInPatient = async (
    appointmentId: number,
    access_code: number | string,
    date: string,
  ) => {
    try {
      return await checkInSchedulePatient({
        siteId,
        date,
        appointmentId,
        access_code,
      }).unwrap()
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  const isLoading = useMemo(() => {
    return isCreating || isUpdating
  }, [isCreating, isUpdating])

  return {
    createAppointment,
    updateAppointment,
    notifyPatient,
    deleteAppointment,
    checkInPatient,
    notifyMultiplePatients,
    isLoading,
    isCreating,
    isUpdating,
    isNotifying,
    isDeleting,
    isCheckingIn,
    isNotifyingMultiple,
  }
}
